/**
 * The two facts about a ticket's / report's invoice (Nick, 2026-10-05: "it's not invoiced until it's SENT"), mirrored from
 * functions/src/billing/invoiceLifecycle.ts. A parity test on the functions side reads this file and fails if they drift.
 *
 *  - invoice CREATED: `billedAt` / `qboInvoiceId` (stamped when the invoice is pushed to QuickBooks; unchanged meaning).
 *  - SENT: `invoiceSentAt` (stamped when the SENT invoice is observed). Only the "7 Billed" status mirror waits on it.
 *
 * A record created through an invoice draft carries `invoiceSentAt: null` until the send is seen (`isCreatedNotSent`);
 * records billed before this change have no `invoiceSentAt` field and are NOT treated as unsent.
 */
type Doc = Record<string, unknown> | undefined | null;

/** An invoice exists (created, sent or not). */
export function hasInvoiceCreated(data: Doc): boolean {
  if (!data) return false;
  const id = data.qboInvoiceId;
  return Boolean(data.invoiceCreatedAt) || (typeof id === 'string' ? id !== '' : id != null);
}

/** Created through an invoice draft and not yet SENT: `invoiceSentAt` is explicitly null (written at create). */
export function isCreatedNotSent(data: Doc): boolean {
  return Boolean(data && data.billedAt && data.billedVia === 'invoice-draft' && data.invoiceSentAt === null);
}

/** Billed or invoiced (created): either way, do not offer it for another invoice. */
export function isBilledOrInvoiced(data: Doc): boolean {
  return Boolean(data && data.billedAt) || hasInvoiceCreated(data);
}
